import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import useSWR, { useSWRConfig } from "swr";
import {
  Stack,
  Text,
  Button,
  Center,
  Container,
  Flex,
  FormLabel,
  Select,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Box,
  Divider,
  Badge,
} from "@chakra-ui/react";
import { QuestionnaireHeader } from "../../components/Form";
import { AttentionModal, SuccessModal } from "../../components/Modals";
import baseService from "../../services/base";
import Loader from "../../components/Feedback/Loader";
import { QuestionCardRespondents } from "../../components/Card";
import { fetchData } from "../../services/fetch";

async function fetchRespondentsSurvey(url) {
  const response = await baseService.get(url);
  return response?.data?.[0];
}

export default function RespondentsEnhanced() {
  const navigate = useNavigate();
  const { id: surveyId } = useParams();
  const params = new URLSearchParams(window.location.search);
  const companyId = params?.get("cr");
  const companyGroupId = params?.get("cg");

  const [respondentsList, setRespondentsList] = useState();
  const [responsiblesData, setResponsiblesData] = useState({});
  const { mutate } = useSWRConfig();

  const { data: surveyData } = useSWR(
    companyId
      ? `/link_user_question/survey/${surveyId}?CompanyGroupId=${companyGroupId}&id_company=${companyId}`
      : null,
    fetchRespondentsSurvey
  );

  const { data: companyUsers } = useSWR(
    companyGroupId ? `user?group=${companyGroupId}` : null,
    fetchData
  );

  // Buscar responsáveis já atribuídos
  const { data: existingResponsibles } = useSWR(
    surveyId ? `/question-responsible/survey/${surveyId}` : null,
    fetchData
  );

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { isDirty, isSubmitting },
  } = useForm();

  const [successModalIsOpen, setSuccessModalIsOpen] = useState(false);
  const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
  const [defaultValues, setDefaultValues] = useState();
  const [initialDefaultValues, setInitialDefaultValues] = useState();

  useEffect(() => {
    if (companyUsers) {
      const respondents =
        companyUsers?.filter((user) => {
          // Filtra usuários com role RESPONDENTE
          const userRoles = user?.roles || [];
          const isRespondent = userRoles.some((role) => role?.name === "RESPONDENTE");
          return isRespondent;
        }) || [];
      setRespondentsList(respondents);
    }
  }, [companyUsers]);

  // Inicializar responsáveis
  useEffect(() => {
    if (existingResponsibles && surveyData?.survey?.questions) {
      const responsiblesMap = {};
      existingResponsibles.forEach((resp) => {
        responsiblesMap[`responsible_${resp.id_question_template}`] =
          resp.id_user;
      });
      setResponsiblesData(responsiblesMap);
      
      // Set form values
      Object.keys(responsiblesMap).forEach((key) => {
        setValue(key, responsiblesMap[key]);
      });
    }
  }, [existingResponsibles, surveyData, setValue]);

  useEffect(() => {
    if (surveyData && surveyData?.survey && respondentsList) {
      const newDefaultValues = {};

      surveyData?.survey?.questions?.forEach((question) => {
        const questionTemplateId = `question_${question.id_question_template}`;

        if (!newDefaultValues[questionTemplateId]) {
          newDefaultValues[questionTemplateId] = [];
        }

        if (!question?.linked_users || question?.linked_users?.length === 0) {
          respondentsList?.forEach((user) => {
            newDefaultValues[questionTemplateId].push(user.id_user.toString());
          });
        } else {
          respondentsList?.forEach((user) => {
            const isUserLinked = question?.linked_users?.some(
              (linkedUser) => linkedUser.id_user === user.id_user
            );
            if (!isUserLinked) {
              newDefaultValues[questionTemplateId].push(
                user.id_user.toString()
              );
            }
          });
        }

        if (!question?.linked_users || question?.linked_users?.length === 0) {
          newDefaultValues[questionTemplateId] = respondentsList?.map((user) =>
            user.id_user.toString()
          );
        }
      });

      setDefaultValues(newDefaultValues);
      setValue("nestedValue", newDefaultValues);
      setInitialDefaultValues(newDefaultValues);
    }
  }, [surveyData, respondentsList, setValue]);

  function differentValues(firstArray, secondArray) {
    return firstArray?.filter((value) => !secondArray?.includes(value));
  }

  async function onSubmit(formData) {
    const values = formData.nestedValue;

    try {
      // 1. Atualizar visibilidade (link_user_question)
      const formattedArray = [];
      for (const key in values) {
        const number = parseInt(key?.split("_")[1]);
        if (Object.prototype.hasOwnProperty.call(initialDefaultValues, key)) {
          const initialValue = initialDefaultValues[key];
          const currentValue = values[key];
          if (
            initialValue !== false &&
            Array.isArray(initialValue) &&
            currentValue
          ) {
            const differentValuesArray = differentValues(
              initialValue,
              currentValue
            );

            if (differentValuesArray.length > 0) {
              differentValuesArray.forEach((val) => {
                formattedArray.push({
                  user: parseInt(val),
                  question_template: number,
                  survey: +surveyId,
                });
              });
            }
          }
        }
        if (key.startsWith("question_") && Array.isArray(values[key])) {
          const questionTemplate = parseInt(key?.split("_")[1]);
          const userValues = values[key];
          userValues?.forEach((val) => {
            if (
              defaultValues?.[`${key}`] === false ||
              !defaultValues?.[`${key}`]?.includes(val)
            ) {
              formattedArray.push({
                user: parseInt(val),
                question_template: questionTemplate,
                survey: +surveyId,
              });
            }
          });
        }
      }

      if (formattedArray.length > 0) {
        await baseService.post("/link_user_question/many", formattedArray);
      }

      // 2. Atualizar responsáveis (question_responsible)
      const responsibleQuestions = [];
      
      // Buscar todas as questões do survey
      if (surveyData?.survey?.questions) {
        surveyData.survey.questions.forEach((question) => {
          const fieldName = `responsible_${question.id_question_template}`;
          const userId = formData[fieldName];
          
          responsibleQuestions.push({
            id_question_template: question.id_question_template,
            id_user: userId && userId !== "" ? parseInt(userId) : null,
          });
        });
      }

      if (responsibleQuestions.length > 0) {
        const payload = {
          id_survey: +surveyId,
          questions: responsibleQuestions,
        };
        
        await baseService.post("/question-responsible/bulk-update", payload);
      }

      setSuccessModalIsOpen(true);
      mutate(
        `/link_user_question/survey/${surveyId}?CompanyGroupId=${companyGroupId}&id_company=${companyId}`
      );
      mutate(`/question-responsible/survey/${surveyId}`);
    } catch (err) {
      console.error("❌ Erro ao salvar:", err);
      console.error("Detalhes:", err.response?.data);
      alert(`Erro ao salvar: ${err.response?.data?.message || err.message}`);
    }
  }

  function closeSuccessModal() {
    setSuccessModalIsOpen(false);
    navigate(-1);
  }

  function openConfirmationModal() {
    setConfirmationModalIsOpen(true);
  }

  function closeConfirmationModal() {
    setConfirmationModalIsOpen(false);
  }

  function confirmAndGoBack() {
    setConfirmationModalIsOpen(false);
    navigate(-1);
  }

  const responsibleOptions = useMemo(() => {
    if (!respondentsList || respondentsList.length === 0) {
      return [{ value: "", label: "Nenhum responsável" }];
    }
    const options = [
      { value: "", label: "Nenhum responsável" },
      ...respondentsList.map((user) => ({
        value: user.id_user.toString(),
        label: user.username,
      })),
    ];
    return options;
  }, [respondentsList]);

  if (!surveyData || !companyUsers) {
    return <Loader />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <QuestionnaireHeader
        tagLabel={surveyData?.survey?.description}
        heading="Gestão de Respondentes"
        description="Configure quem pode responder cada questão e atribua responsáveis"
      >
        <Stack spacing="3" display={["none", "flex"]}>
          <Button
            type="submit"
            variant="primary"
            w="271px"
            height="40px"
            isDisabled={!isDirty}
            isLoading={isSubmitting}
          >
            Salvar Configurações
          </Button>
          <Button variant="cancel" onClick={openConfirmationModal}>
            Voltar
          </Button>
        </Stack>
      </QuestionnaireHeader>

      {!surveyData?.survey?.questions && (
        <Flex mt="32px" justify="center" w="full">
          <Text fontSize="24px" fontWeight="500">
            Não existem perguntas para esse questionário.
          </Text>
        </Flex>
      )}

      {!!defaultValues && (
        <Center px={{ xl: "86px", "2xl": "unset" }}>
          <Container
            maxW={"1280px"}
            p={["0 24px", "0 0 32px"]}
            overflowX="hidden"
          >
            <Box
              bg="#E6F1F8"
              p="16px"
              borderRadius="8px"
              mb="24px"
              mt="24px"
            >
              <Text color="#0073BB" fontWeight={600} fontSize="14px">
                ℹ️ Instruções:
              </Text>
              <Text fontSize="13px" mt="8px" color="#555">
                • <strong>Respondentes:</strong> Marque quem poderá visualizar e responder cada questão
              </Text>
              <Text fontSize="13px" mt="4px" color="#555">
                • <strong>Responsável:</strong> Defina um responsável principal que receberá lembretes automáticos
              </Text>
            </Box>

            {surveyData?.survey?.questions?.map((question, index) => (
              <QuestionCardRespondents
                key={index}
                index={index}
                questionId={question?.id_question}
                questionType={question?.code || ""}
                question={question?.description}
                idPillar={question?.id_pillar}
                idTheme={question?.id_theme}
                pillarName={question?.pillar_name}
                themeTitle={question?.theme_title}
                gri={question?.gri}
              >
                <Stack spacing="20px" w="full">
                  {/* Responsável Principal */}
                  <Box>
                    <Flex align="center" gap="8px" mb="12px">
                      <Text fontWeight={600} fontSize="14px" color="#333">
                        👤 Responsável Principal
                      </Text>
                      <Badge colorScheme="blue" fontSize="11px">
                        Receberá alertas
                      </Badge>
                    </Flex>
                    <Controller
                      name={`responsible_${question.id_question_template}`}
                      control={control}
                      defaultValue={
                        responsiblesData[
                          `responsible_${question.id_question_template}`
                        ] || ""
                      }
                      render={({ field }) => (
                        <Select
                          {...field}
                          placeholder="Selecionar responsável"
                          size="md"
                          bg="white"
                          borderColor="#cbd5e0"
                          _hover={{ borderColor: "#0073BB" }}
                          _focus={{
                            borderColor: "#0073BB",
                            boxShadow: "0 0 0 1px #0073BB",
                          }}
                        >
                          {responsibleOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                  </Box>

                  <Divider />

                  {/* Respondentes */}
                  <Box>
                    <Text fontWeight={600} fontSize="14px" color="#333" mb="12px">
                      👥 Respondentes Autorizados
                    </Text>
                    <Flex gap="12px" flexWrap="wrap" className="questionary-app">
                      {respondentsList?.map((companyUser, i) => (
                        <Flex
                          key={i}
                          height="40px"
                          align="center"
                          justify="center"
                          px="16px"
                          borderRadius="48px"
                          overflowX="hidden"
                          pos="relative"
                        >
                          <input
                            type="checkbox"
                            value={companyUser?.id_user}
                            {...register(
                              `nestedValue.question_${question.id_question_template.toString()}`
                            )}
                            id={`nestedValue.question_${question.id_question_template.toString()}_${
                              companyUser?.id_user
                            }`}
                            className="questionary-checkbox"
                          />
                          <FormLabel
                            fontSize="sm"
                            htmlFor={`nestedValue.question_${question.id_question_template.toString()}_${
                              companyUser?.id_user
                            }`}
                            top="4px"
                            left="6px"
                            pos="relative"
                            zIndex={4}
                            pointerEvents={"none"}
                          >
                            {companyUser?.username}
                          </FormLabel>
                        </Flex>
                      ))}
                    </Flex>
                  </Box>
                </Stack>
              </QuestionCardRespondents>
            ))}
          </Container>
        </Center>
      )}
      
      <AttentionModal
        isOpen={confirmationModalIsOpen}
        onClose={closeConfirmationModal}
        submit={confirmAndGoBack}
        title="Confirme as configurações"
        content="Verifique se os respondentes e responsáveis foram atribuídos corretamente."
        buttonText="Corrigir"
      />

      <SuccessModal
        isOpen={successModalIsOpen}
        onClose={closeSuccessModal}
        title="Configurações salvas com sucesso!"
      />
    </form>
  );
}
